import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { spacing, borderRadius, animation, sizing, typography, colors } from '../../theme';

type LanguageSwitcherVariant = 'compact' | 'card';

interface LanguageSwitcherProps {
  variant?: LanguageSwitcherVariant;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'compact' }) => {
  const { i18n, t } = useTranslation();
  const currentLang: 'ro' | 'en' = i18n.language?.startsWith('ro') ? 'ro' : 'en';

  const setLang = (lang: 'ro' | 'en') => {
    if (lang !== currentLang) i18n.changeLanguage(lang);
  };

  if (variant === 'card') {
    return (
      <CardWrapper role="radiogroup" aria-label={t('settings.language')}>
        <ChoiceButton
          type="button"
          role="radio"
          aria-checked={currentLang === 'en'}
          $active={currentLang === 'en'}
          onClick={() => setLang('en')}
        >
          <LangBadge>EN</LangBadge>
          <LangName>{t('settings.languageEnglish')}</LangName>
        </ChoiceButton>
        <ChoiceButton
          type="button"
          role="radio"
          aria-checked={currentLang === 'ro'}
          $active={currentLang === 'ro'}
          onClick={() => setLang('ro')}
        >
          <LangBadge>RO</LangBadge>
          <LangName>{t('settings.languageRomanian')}</LangName>
        </ChoiceButton>
      </CardWrapper>
    );
  }

  return (
    <SwitchButton
      onClick={() => setLang(currentLang === 'en' ? 'ro' : 'en')}
      aria-label={`Switch language to ${currentLang === 'en' ? 'Romanian' : 'English'}`}
      title={currentLang === 'en' ? 'Switch to Romanian' : 'Switch to English'}
    >
      <LangCode>{currentLang.toUpperCase()}</LangCode>
    </SwitchButton>
  );
};

const SwitchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${sizing.button.lg}px;
  height: ${sizing.button.lg}px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: ${borderRadius.md}px;
  cursor: pointer;
  color: var(--color-text-primary, #FFFFFF);
  transition: all ${animation.duration.normal} ${animation.easing.default};

  &:hover {
    background: rgba(139, 95, 255, 0.15);
    border-color: rgba(139, 95, 255, 0.3);
    transform: translateY(-2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(139, 95, 255, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LangCode = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  user-select: none;
`;

const CardWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.md}px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ChoiceButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  padding: ${spacing.lg}px ${spacing.xl}px;
  background: ${({ $active }) =>
    $active ? 'rgba(139, 92, 246, 0.18)' : 'rgba(139, 92, 246, 0.04)'};
  border: 1px solid ${({ $active }) =>
    $active ? 'rgba(139, 92, 246, 0.55)' : 'rgba(139, 92, 246, 0.18)'};
  border-radius: ${borderRadius.md}px;
  color: #ffffff;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.base};
  font-weight: ${({ $active }) => ($active ? typography.fontWeight.semibold : typography.fontWeight.regular)};
  cursor: pointer;
  transition: all ${animation.duration.normal} ${animation.easing.default};
  text-align: left;

  &:hover {
    background: rgba(139, 92, 246, 0.15);
    border-color: rgba(139, 92, 246, 0.45);
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.25);
  }

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
    background: ${({ $active }) =>
      $active ? 'rgba(139, 92, 246, 0.14)' : 'rgba(139, 92, 246, 0.05)'};
    border-color: ${({ $active }) =>
      $active ? 'rgba(139, 92, 246, 0.5)' : 'rgba(139, 92, 246, 0.22)'};
  }
`;

const LangBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  padding: ${spacing.xs}px ${spacing.sm}px;
  background: rgba(139, 92, 246, 0.2);
  border: 1px solid rgba(139, 92, 246, 0.4);
  border-radius: ${borderRadius.sm}px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 1px;
`;

const LangName = styled.span`
  flex: 1;
`;
